import React, { Component } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import {
  Grid, Row, Col, Card, Table, Input, Tag, Button,
  Drawer, Descriptions, Space, Empty, Typography,
} from 'antd';

import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config/api';

const { useBreakpoint } = Grid;
const { Text } = Typography;

class OrderSets extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      filteredItems: [],
      loading: false,
      searchText: '',

      drawerVisible: false,
      selectedTest: null,
    };
  }

  componentDidMount = () => {
    this.loadOrderSets();
  }

  loadOrderSets = async () => {
    const token = await authService.ensureValidToken();
    if (!token) { console.error('Not authenticated'); return; }

    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/orderSets/search`,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      this.setState({
        items: res.data.items,
        filteredItems: res.data.items,
        loading: false,
      });
    }).catch(err => {
      console.log(err);
      this.setState({ loading: false });
    });
  }

  handleSearch = (e) => {
    const searchText = e.target.value;
    const { items } = this.state;

    if (!searchText) {
      this.setState({ searchText, filteredItems: items });
      return;
    }

    const lower = searchText.toLowerCase();
    const filteredItems = items.filter(item =>
      (item.Description || '').toLowerCase().includes(lower) ||
      (item.OrderedTest || '').toLowerCase().includes(lower)
    );

    this.setState({ searchText, filteredItems });
  }

  openDetails = (record) => {
    this.setState({ drawerVisible: true, selectedTest: record });
  }

  closeDetails = () => {
    this.setState({ drawerVisible: false, selectedTest: null });
  }

  getSampleTagColor = (type) => {
    if (!type) return 'default';
    const upper = type.toUpperCase();
    const colors = {
      BLOOD: 'blue',
      SERUM: 'blue',
      URINE: 'gold',
      PLASMA: 'cyan',
    };
    return colors[upper] || 'blue';
  }

  renderMobileCards = () => {
    const { filteredItems, loading } = this.state;

    if (loading) {
      return <Card loading={true} />;
    }

    if (filteredItems.length === 0) {
      return <Empty description="No tests found" />;
    }

    return filteredItems.map((item, index) => (
      <Card
        key={item.id || index}
        size="small"
        style={{ marginBottom: 12 }}
      >
        <Row justify="space-between" align="top">
          <Col flex="auto">
            <Text strong>{item.Description}</Text>
          </Col>
          <Col>
            <Tag color="red">{item.OrderedTest}</Tag>
          </Col>
        </Row>

        <div style={{ margin: '8px 0' }}>
          <Space size={[4, 4]} wrap>
            {item.PreferredSpecimen && (
              <Tag color={this.getSampleTagColor(item.PreferredSpecimen)} style={{ fontWeight: 600 }}>
                {item.PreferredSpecimen.toUpperCase()}
              </Tag>
            )}
          </Space>
        </div>
        <Button
          type="primary"
          block
          style={{ marginTop: 12 }}
          onClick={() => this.openDetails(item)}
        >
          View Full Details
        </Button>
      </Card>
    ));
  }

  renderDetailsDrawer = () => {
    const { drawerVisible, selectedTest } = this.state;
    const { screens } = this.props;
    const isMobile = !screens.md;

    if (!selectedTest) return null;

    return (
      <Drawer
        title={selectedTest.Description}
        placement={isMobile ? 'bottom' : 'right'}
        onClose={this.closeDetails}
        open={drawerVisible}
        width={isMobile ? '100%' : 780}
        height={isMobile ? '90%' : undefined}
        styles={{
          body: { padding: isMobile ? '12px' : '24px' },
        }}
      >
        <Descriptions
          column={1}
          bordered={!isMobile}
          layout={isMobile ? 'vertical' : 'horizontal'}
          size="small"
        >
          <Descriptions.Item label="Code">{selectedTest.OrderedTest}</Descriptions.Item>
          <Descriptions.Item label="Description">{selectedTest.Description}</Descriptions.Item>
          {selectedTest.PreferredSpecimen && (
            <Descriptions.Item label="Sample Type">
              <Tag color={this.getSampleTagColor(selectedTest.PreferredSpecimen)} style={{ fontWeight: 600 }}>
                {selectedTest.PreferredSpecimen.toUpperCase()}
              </Tag>
            </Descriptions.Item>
          )}

          {selectedTest.TransportContainer && (
            <Descriptions.Item label="Container">{selectedTest.TransportContainer}</Descriptions.Item>
          )}

          {selectedTest.AlternativeSpecimen && (
            <Descriptions.Item label="Alternative Specimen">{selectedTest.AlternativeSpecimen.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.Collectioninstructions && (
            <Descriptions.Item label="Collection Instructions">{selectedTest.Collectioninstructions.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.TransportTemperature && (
            <Descriptions.Item label="Shipping Temperature">{selectedTest.TransportTemperature.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.RejectionCriteria && (
            <Descriptions.Item label="Rejection Criteria">{selectedTest.RejectionCriteria.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.SpecimenStability && (
            <Descriptions.Item label="Specimen Stability">{selectedTest.SpecimenStability.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.TestPurpose && (
            <Descriptions.Item label="Test Purpose">{selectedTest.TestPurpose.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.TestMethodology && (
            <Descriptions.Item label="Methodology">{selectedTest.TestMethodology.replace(/\/\.\/ /g, ', ')}</Descriptions.Item>
          )}

          {selectedTest.TurnaroundTime && (
            <Descriptions.Item label="TAT">{selectedTest.TurnaroundTime}</Descriptions.Item>
          )}
        </Descriptions>

        {selectedTest.PerformedTests && selectedTest.PerformedTests.length > 1 && (
          <div style={{ marginTop: 24 }}>
            <Text strong style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
              Individual Components ({selectedTest.PerformedTests.length})
            </Text>
            <Table
              dataSource={selectedTest.PerformedTests}
              columns={[
                { title: 'Code', dataIndex: 'Code', key: 'Code', width: 120 },
                { title: 'Description', dataIndex: 'Description', key: 'Description' },
              ]}
              size="small"
              pagination={false}
              rowKey={(row) => row.Code}
            />
          </div>
        )}
      </Drawer>
    );
  }

  render() {
    const { screens } = this.props;
    const isMobile = !screens.md;
    const { filteredItems, loading } = this.state;

    const columns = [
      {
        title: 'Code',
        dataIndex: 'OrderedTest',
        key: 'OrderedTest',
      },
      {
        title: 'Test Name',
        dataIndex: 'Description',
        key: 'Description',
        sorter: (a, b) => (a.Description || '').localeCompare(b.Description || ''),
      },      
      {
        title: 'Specimen Required',
        dataIndex: 'PreferredSpecimen',
        key: 'PreferredSpecimen',
        responsive: ['md'],
        render: (value) => value ? (
          <Tag 
            color={this.getSampleTagColor(value)} 
            style={{ fontWeight: 600 }}>
            {value.toUpperCase()}
          </Tag>
        ) : null,
      },
      {
        title: '',
        key: 'action',
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => this.openDetails(record)}
          >
            Details
          </Button>
        ),
      },
    ];

    // Mobile column definition
    const mobileColumns = [
      {
        title: 'Test',
        key: 'test',
        render: (_, item) => (
          <Card size="small" style={{ margin: 0 }}>
            <Row justify="space-between" align="top">
              <Col flex="auto">
                <Text strong>{item.Description}</Text>
              </Col>
              <Col>
                <Tag color="red">{item.OrderedTest}</Tag>
              </Col>
            </Row>
            <div style={{ margin: '8px 0' }}>
              <Space size={[4, 4]} wrap>
                {item.PreferredSpecimen && (
                  <Tag 
                    color={this.getSampleTagColor(item.PreferredSpecimen)} 
                    style={{
                      fontWeight: 600,
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      height: 'auto',
                    }}
                  >
                    {item.PreferredSpecimen.toUpperCase()}
                  </Tag>
                )}
              </Space>
            </div>
            <Button
              type="primary"
              block
              style={{ marginTop: 12 }}
              onClick={() => this.openDetails(item)}
            >
              View Full Details
            </Button>
          </Card>
        ),
      },
    ];

    return (
      <Row>
        <Col span={24}>
          <Card
            title="Test Directory"
            style={{ width: '100%' }}
            extra={
              !isMobile && (
                <Input
                  placeholder="Search for a test..."
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={this.handleSearch}
                  style={{ width: 300 }}
                />
              )
            }
          >
            {isMobile && (
              <Input
                placeholder="Search for a test..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={this.handleSearch}
                size="large"
                style={{ marginBottom: 16 }}
              />
            )}

            <Table
              columns={isMobile ? mobileColumns : columns}
              dataSource={filteredItems}
              loading={loading}
              size="small"
              rowKey={(row) => row.id}
              pagination={{ pageSize: isMobile ? 5 : 10, showSizeChanger: !isMobile }}
              showHeader={!isMobile}
            />
          </Card>
        </Col>
        {this.renderDetailsDrawer()}
      </Row>
    );
  }
}

export default function OrderSetsWithBreakpoints(props) {
  const { clientCode } = useParams();
  const screens = useBreakpoint();
  return <OrderSets {...props} clientCode={clientCode} screens={screens} />;
}
